/* eslint-disable react-hooks/rules-of-hooks */
import { LRUCache } from "lru-cache";
import { makeAutoObservable, runInAction } from "mobx";
import { Channel, Message, Nullable } from "revolt.js";

import { calcMissingReadMarks } from "../calcMissingReadMarks";
import { EventBus } from "../events";
import { TIME } from "../time";
import { RenderingProxy } from "./proxy";

export type RendererParams = {
    pinned?: boolean;
};

export const SMOOTH_SCROLL_ON_RECEIVE = false;

export const MESSAGE_LIMIT = 1000;
export const MESSAGE_ID_LENGTH = 26; // ULID message ids; the length check distinguishes message rows from preloader/divider children
export const API_PAGE_SIZE = 50; // fetchMessagesWithUsers default page size; `length < 50` means no more history
export const NEARBY_FETCH_LIMIT = 100; // deep-link nearby init window size

/**
 * Merges a freshly fetched page with the feed still on screen. The keep-feed
 * refresh stays RENDER while the newest page is in flight, so rows the socket
 * appended meanwhile — and the rows still on screen — must survive the swap.
 * ULID ascending is chronological; the result is capped at MESSAGE_LIMIT.
 */
function mergePages(fetched: Message[], live: Message[]) {
    const ids = new Set(fetched.map((m) => m._id));
    const extra = live.filter((m) => !ids.has(m._id));
    if (extra.length === 0) return fetched;

    return [...fetched, ...extra]
        .sort((a, b) => a._id.localeCompare(b._id))
        .slice(-MESSAGE_LIMIT);
}

export class ChannelRenderer {
    channel: Channel;

    state: "LOADING" | "WAITING_FOR_NETWORK" | "EMPTY" | "RENDER" = "LOADING";
    // Scroll target for the area after a (re)fetch: a plain refresh lands at
    // the bottom, a deep link on its message.
    scrollState:
        { type: "ScrollToBottom" } | { type: "ScrollToMessage"; id: string } = {
        type: "ScrollToBottom",
    };
    atTop: Nullable<boolean> = null;
    messages: Message[] = [];

    stale = false;
    // Independent top/bottom fetch latches: a top and a bottom history
    // load may be in flight at the same time.
    fetchingTop = false;
    fetchingBottom = false;

    // Зачем это все что оно делает???
    initInFlight = false;
    // Deep-link wait: a nearby fetch requested while the socket was
    // down; consumed by the next init() so the session-online re-init
    // restores the message view instead of the latest 50.
    pendingNearby?: string;

    suggestJumpToBottom = false;

    private _params?: RendererParams;

    proxy: RenderingProxy;

    get isPinned() {
        return this._params?.pinned;
    }

    constructor(channel: Channel, params?: RendererParams) {
        this.channel = channel;
        this._params = params;

        this.proxy = new RenderingProxy(this);

        makeAutoObservable(this);

        this.receive = this.receive.bind(this);
        this.updated = this.updated.bind(this);
        this.delete = this.delete.bind(this);
        this.onMessageRead = this.onMessageRead.bind(this);

        const client = this.channel.client;
        client.addListener("message", this.receive);
        client.addListener("message/updated", this.updated);
        client.addListener("message/delete", this.delete);
        client.addListener("message/read", this.onMessageRead);
    }

    destroy() {
        const client = this.channel.client;
        client.removeListener("message", this.receive);
        client.removeListener("message/updated", this.updated);
        client.removeListener("message/delete", this.delete);
        client.removeListener("message/read", this.onMessageRead);
    }

    async jumpToBottom(smooth: boolean) {
        if (this.state === "RENDER") {
            EventBus.ui.messageArea.jumpToBottom.emit(smooth);
        } else {
            await this.doInit(undefined);
        }
    }

    private receive(message: Message) {
        if (message.channel_id !== this.channel._id) return;
        if (this.messages.find((x) => x._id === message._id)) return;

        let messages = [...this.messages, message];
        let atTop = this.atTop;
        if (messages.length > MESSAGE_LIMIT) {
            messages = messages.slice(messages.length - MESSAGE_LIMIT);
            atTop = false;
        }

        runInAction(() => {
            this.messages = messages;
            this.atTop = atTop;
        });
    }

    private updated(message: Message) {
        if (this.isPinned) {
            /**
             * Весь вот этот код нужен чтобы найти позицию сообщения для прикрепленных сообщений и
             * он нужен ТОЛЬКО для прикрепленных сообщений
             */
            const found = this.messages.find((msg) => msg._id === message._id);
            if (message.pinned && !found) {
                this.channel
                    .fetchMessagesWithUsers({
                        pinned: true,
                        before: message._id,
                        limit: 1,
                    })
                    .then((messagesBefore) => {
                        const [msgBefore] = messagesBefore.messages;
                        if (!msgBefore) {
                            this.messages = [message, ...this.messages];
                            return;
                        }

                        const messageBeforeIndex = this.messages.findIndex(
                            (msg) => msg._id === msgBefore._id,
                        );
                        if (messageBeforeIndex === -1) {
                            return;
                        }

                        const newMessages = [...this.messages];
                        newMessages.splice(messageBeforeIndex + 1, 0, message);
                        this.messages = newMessages;
                    });
            } else if (!message.pinned && found) {
                this.messages = this.messages.filter(
                    (msg) => msg._id !== message._id,
                );
            }
        }
    }

    private delete(id: string) {
        const channel = this.channel;
        if (!channel) return;
        if (this.state !== "RENDER") return;

        const index = this.messages.findIndex((x) => x._id === id);

        if (index > -1) {
            runInAction(() => {
                this.messages.splice(index, 1);
            });
        }
    }

    private onMessageRead(packet: any) {
        if (packet.channel_id !== this.channel._id) return;
        const msg = this.channel.client.messages.get(packet.message_id);
        if (msg && this.state === "RENDER" && msg.readMarks.length > 0) {
            calcMissingReadMarks(
                this.channel.client,
                packet.channel_id,
                new Set(this.messages.map((m) => m._id)),
            );
        }
    }

    async init(message_id?: string) {
        // Deep-link wait: restore the parked nearby target (set by doInit's
        // WAITING_FOR_NETWORK branch) so a bare re-init finds the message.
        if (!message_id && this.pendingNearby) {
            message_id = this.pendingNearby;
            this.pendingNearby = undefined;
        }

        // A refresh of an on-screen feed must not blank it; deep links and
        // pinned feeds replace the window on purpose and still show the loader.
        const keepFeed =
            !message_id &&
            !this.isPinned &&
            this.state === "RENDER" &&
            this.messages.length > 0;

        // keepFeed: a refresh no longer passes through LOADING, so dedupe by
        // in-flight alone — a second Online blip must not fire a 3rd fetch.
        if (!message_id && this.initInFlight) {
            return;
        }

        // Nearby init (message_id) also marks the fetch in flight; the guard
        // above stays message_id-only, so nearby clicks behave as before.
        this.initInFlight = true;

        this.stale = false;
        this.state = keepFeed ? "RENDER" : "LOADING";
        this.scrollState = message_id
            ? { type: "ScrollToMessage", id: message_id }
            : { type: "ScrollToBottom" };
        this.doInit(message_id, keepFeed);
    }

    markStale() {
        this.stale = true;
    }

    complete() {
        this.fetchingTop = false;
        this.fetchingBottom = false;
    }

    async reloadStale() {
        if (this.stale) {
            this.stale = false;
            await this.init();
        }
    }

    async loadTop() {
        if (this.fetchingTop) return;
        this.fetchingTop = true;

        try {
            await this.doLoadTop();
        } catch (e) {
            console.error("ChannelRenderer.loadTop", e);
        } finally {
            runInAction(() => {
                this.fetchingTop = false;
            });
        }
    }

    async loadBottom() {
        if (this.fetchingBottom) return;
        this.fetchingBottom = true;

        try {
            await this.doLoadBottom();
        } catch (e) {
            console.error("ChannelRenderer.loadBottom", e);
        } finally {
            runInAction(() => {
                this.fetchingBottom = false;
            });
        }
    }

    private async doInit(nearby?: string, keepFeed = false) {
        if (this.channel.client.websocket.connected) {
            let messagesSource: Promise<Message[]>;

            if (nearby) {
                messagesSource = this.channel
                    .fetchMessagesWithUsers({
                        nearby,
                        limit: NEARBY_FETCH_LIMIT,
                        pinned: this.isPinned,
                    })
                    .then(({ messages }) => {
                        messages.sort((a, b) => a._id.localeCompare(b._id));

                        runInAction(() => {
                            this.state = "RENDER";
                            this.initInFlight = false;
                            this.messages = messages;
                            this.atTop = messages.length < NEARBY_FETCH_LIMIT;
                        });
                        return messages;
                    });
            } else {
                messagesSource = this.channel
                    .fetchMessagesWithUsers({ pinned: this.isPinned })
                    .then(({ messages }) => {
                        messages.reverse();

                        runInAction(() => {
                            this.state = "RENDER";
                            this.initInFlight = false;
                            this.messages = keepFeed
                                ? mergePages(messages, this.messages)
                                : messages;
                            this.atTop = messages.length < API_PAGE_SIZE;
                        });
                        return messages;
                    });
            }

            await messagesSource
                .catch((e) => this.failInit(e))
                .then((messages) => {
                    const messageIds = messages.map((x) => x._id);
                    return this.channel
                        .fetchReadsVec(messageIds)
                        .finally(() => {
                            calcMissingReadMarks(
                                this.channel.client,
                                this.channel._id,
                                new Set(messageIds),
                            );
                        });
                });
        } else {
            runInAction(() => {
                this.state = "WAITING_FOR_NETWORK";
                this.initInFlight = false;
                // Park the deep-link target across the network wait (the
                // session-online re-init is a bare init() — without this it
                // fetches the latest 50 and the message is lost).
                if (nearby) this.pendingNearby = nearby;
            });
        }
    }

    private failInit(e: unknown): never {
        runInAction(() => {
            this.state = "EMPTY";
            this.initInFlight = false;
            // Failed init is a failure state: mark stale so
            // the next session Online re-inits via reloadStale.
            this.stale = true;
        });
        throw e;
    }

    private async doLoadTop() {
        const channel = this.channel;
        if (!channel) return true;

        if (this.state !== "RENDER") return true;
        if (this.atTop) return true;

        const { messages: data } = await this.channel.fetchMessagesWithUsers({
            pinned: this.isPinned,
            before: this.messages[0]._id,
        });

        runInAction(() => {
            if (data.length === 0) {
                this.atTop = true;
                return;
            }

            data.reverse();
            this.messages = [...data, ...this.messages];

            if (data.length < API_PAGE_SIZE) {
                this.atTop = true;
            }

            if (this.messages.length > MESSAGE_LIMIT) {
                // Keep the NEWEST tail — slice(0, LIMIT) kept the oldest
                // 1000 and evicted the newest (the tail is intact, so
                // the st Bottom/Free state stays valid).
                this.messages = this.messages.slice(-MESSAGE_LIMIT);
            }
        });

        // Fetch read marks for all currently loaded messages before back-propagation.
        const allIds = this.messages.map((m) => m._id);
        channel.fetchReadsVec(allIds).then(() => {
            calcMissingReadMarks(channel.client, channel._id, new Set(allIds));
        });
    }

    private async doLoadBottom() {
        const channel = this.channel;
        if (!channel) return true;

        if (this.state !== "RENDER") return true;

        const { messages: data } = await this.channel.fetchMessagesWithUsers({
            pinned: this.isPinned,
            after: this.messages[this.messages.length - 1]._id,
            sort: "Oldest",
        });

        runInAction(() => {
            if (data.length === 0) {
                return;
            }

            this.messages.splice(this.messages.length, 0, ...data);

            if (this.messages.length > MESSAGE_LIMIT) {
                this.messages.splice(0, this.messages.length - MESSAGE_LIMIT);
                this.atTop = false;
            }
        });

        const allIds = this.messages.map((m) => m._id);
        channel.fetchReadsVec(allIds).then(() => {
            calcMissingReadMarks(channel.client, channel._id, new Set(allIds));
        });
    }
}

class RendererCache {
    private _cache: LRUCache<string, ChannelRenderer>;

    constructor(cache: LRUCache<string, ChannelRenderer>) {
        this._cache = cache;
    }

    get(channel: Channel, params?: RendererParams) {
        let renderer = this._cache.get(channel._id);
        if (!renderer) {
            renderer = new ChannelRenderer(channel, params);
            this._cache.set(channel._id, renderer);
        }
        return renderer;
    }

    remove(channelId: string) {
        this._cache.delete(channelId);
    }

    get size() {
        return this._cache.size;
    }
}

const opts = {
    max: 10,
    // Every removal path (TTL expiry, capacity eviction, deleteRenderer)
    // destroys the renderer — its WS listeners must not keep mutating
    // orphan state.
    dispose: (r: ChannelRenderer) => r.destroy(),
};

const renderers = new RendererCache(
    new LRUCache<string, ChannelRenderer>(opts),
);

export function getRenderer(channel: Channel) {
    return renderers.get(channel);
}

export function deleteRenderer(channel_id: string) {
    renderers.remove(channel_id);
}

const pinnedRenderers = new RendererCache(
    new LRUCache<string, ChannelRenderer>(opts),
);

export function getPinnedRenderer(channel: Channel) {
    return pinnedRenderers.get(channel, { pinned: true });
}

export function deletePinnedRenderer(channel: Channel) {
    pinnedRenderers.remove(channel._id);
}
